import React from "react";
import {
    ResponsiveContainer,
    BarChart,
    RadarChart,
    PieChart,
    Tooltip,
    Legend,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    CartesianGrid,
    XAxis,
    YAxis,
    Radar,
    Bar,
    Area,
    Pie,
    Cell, AreaChart,
} from "recharts";
import {Card, Select, Space, Tabs, theme} from "antd";
import {LineChartOutlined} from "@ant-design/icons";
import {PageSection} from '@dowty/ui';
import {ChartDataPoint} from "../Pages/StatisticPage";
import {chartColor} from '../utils/chartColors';
import {isGerman} from '../utils/language';
import dayjs from 'dayjs';

interface ReusableChartProps {
    selectedUsers: string[];
    handleUserChange: (value: string[]) => void;
    selectableUsers: string[];
    showData: ChartDataPoint[];
    ouPapiWheresMyColor: (value: string) => string;
    lineChartInclude: boolean;
    headerText: string;
    theme: string;
}

const ReusableChart: React.FC<ReusableChartProps> = ({
    selectedUsers, handleUserChange, selectableUsers, showData, ouPapiWheresMyColor, lineChartInclude, headerText, theme: themeProp}) => {
    const { token } = theme.useToken();
    const chartTheme = themeProp === 'dark' ? 'dark' : 'light';
    const dateFormat = isGerman() ? 'DD.MM.YYYY' : 'MMM D';
    const formatAxisTick = (value: string) => lineChartInclude ? dayjs(value).format(dateFormat) : value;
    const axisTick = {fill: token.colorTextSecondary, fontSize: 12};
    const currentTooltipStyle = {
        contentStyle: {backgroundColor: token.colorBgElevated, color: token.colorText},
        itemStyle: {color: token.colorText},
        stroke: token.colorBorder,
    };
    const chartTabs = [
        ...(lineChartInclude ? [{
            key: '1',
            label: 'Line Chart',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={showData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary}/>
                        <XAxis dataKey="date" stroke={token.colorBorderSecondary} tick={axisTick} tickFormatter={formatAxisTick}/>
                        <YAxis stroke={token.colorBorderSecondary} tick={axisTick}/>
                        <Tooltip contentStyle={currentTooltipStyle.contentStyle}/>
                        <Legend wrapperStyle={{color: token.colorTextSecondary}}/>
                        {selectedUsers.map((user) => (
                            <Area
                                key={user}
                                type="linear"
                                dataKey={user}
                                stroke={ouPapiWheresMyColor(user)}
                                fill={ouPapiWheresMyColor(user)}
                                fillOpacity={0.15}
                                activeDot={{r: 8}}
                                name={user}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            ),
        }] : []),
        {
            key: '2',
            label: 'Bar Chart',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={showData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                        <XAxis dataKey="date" stroke={token.colorBorderSecondary} tick={axisTick} tickFormatter={formatAxisTick} />
                        <YAxis stroke={token.colorBorderSecondary} tick={axisTick} />
                        <Tooltip contentStyle={currentTooltipStyle.contentStyle}/>
                        <Legend wrapperStyle={{color: token.colorTextSecondary}} />
                        {selectedUsers.map((user) => (
                            <Bar
                                stroke={currentTooltipStyle.stroke}
                                strokeWidth={1}
                                key={user}
                                dataKey={user}
                                fill={ouPapiWheresMyColor(user)}
                                name={user}

                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            ),
        },
        {
            key: '3',
            label: 'Radar Chart',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <RadarChart outerRadius="80%" data={showData}>
                        <PolarGrid stroke={token.colorBorderSecondary} />
                        <PolarAngleAxis dataKey="date" tick={axisTick} tickFormatter={formatAxisTick} />
                        <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={axisTick} />
                        <Tooltip contentStyle={currentTooltipStyle.contentStyle}/>
                        <Legend wrapperStyle={{color: token.colorTextSecondary}} />
                        {selectedUsers.map((user) => (
                            <Radar
                                key={user}
                                name={user}
                                dataKey={user}
                                stroke={ouPapiWheresMyColor(user)}
                                fill={ouPapiWheresMyColor(user)}
                                fillOpacity={0.6}
                            />
                        ))}
                    </RadarChart>
                </ResponsiveContainer>
            ),
        },
        {
            key: '4',
            label: 'Pie Chart',
            children: (
                <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                        <Pie
                            data={showData.map((data) => ({
                                name: data.date,
                                value: selectedUsers.reduce((total, user) => total + (typeof data[user] === 'number' ? data[user] as number : 0), 0),
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label
                        >
                            {showData.map((entry, index) => (
                                <Cell key={entry.date} fill={chartColor(index, chartTheme)} />
                            ))}
                        </Pie>
                        <Tooltip contentStyle={currentTooltipStyle.contentStyle}
                                 itemStyle={currentTooltipStyle.itemStyle}
                        />
                        <Legend wrapperStyle={{color: token.colorTextSecondary}} />
                    </PieChart>
                </ResponsiveContainer>
            ),
        }
    ]


    return (
        <Card style={{ marginBottom: 24, borderTop: `2px solid ${token.colorPrimaryBorder}` }}>
            <PageSection
                title={<Space><LineChartOutlined aria-hidden style={{ color: token.colorPrimary, fontSize: 16 }} />{headerText}</Space>}
                actions={
                    <Select
                        value={selectedUsers}
                        style={{ minWidth: 160 }}
                        mode="multiple"
                        onChange={handleUserChange}
                        placeholder="Select users"
                        aria-label={isGerman() ? 'Nutzer auswählen' : 'Select users'}
                    >
                        {selectableUsers.map((user) => (
                            <Select.Option key={user} value={user}>{user}</Select.Option>
                        ))}
                    </Select>
                }
            >
            <Tabs
                defaultActiveKey="1"
                className="sf-chart-tabs"
                style={{ marginBottom: 8 }}
                items={chartTabs}
                moreIcon={<span aria-label={isGerman() ? 'Weitere Diagrammtypen' : 'More chart types'}>…</span>}
            />
            <table className="ui-sr-only">
                <caption>{headerText} {isGerman() ? 'Daten' : 'data'}</caption>
                <thead><tr><th scope="col">{isGerman() ? 'Datum' : 'Date'}</th>{selectedUsers.map(user => <th key={user} scope="col">{user}</th>)}</tr></thead>
                <tbody>{showData.map((point, index) => <tr key={`${point.date}-${index}`}>
                    <th scope="row">{point.date}</th>
                    {selectedUsers.map(user => <td key={user}>{point[user] ?? '—'}</td>)}
                </tr>)}</tbody>
            </table>
            </PageSection>
        </Card>
    )
};
export default ReusableChart;
